import { SAMPLE_COMPANIES } from '../../components/FrostedGate/sample-companies';
import {
  createConversionRateDataModel,
  IConversionRateDataModel,
  IDealsGroupedDataModel,
} from '../../data-models/deal-conversion.data-model';
import { createDealStatItemDataModel, IDealStatsDataModel } from '../../data-models/deal-stats.data-model';
import { createDealTypeDataModel } from '../../data-models/deal-type.data-model';
import { createDealDataModel, IDealDataModel } from '../../data-models/deal.data-model';
import { DealBoardConfig, DealBoardDealCategory } from '../../types';
import { IDealStageDataModel } from './data-models/dealStage.data-model';

export interface IPreviewPipelineColumn {
  title: string;
  deals: IDealDataModel[];
}

const deal = (index: number, stageId: number, gcAmountMax: number, raiseAmount: number): IDealDataModel =>
  createDealDataModel({
    id: -800_001 - index,
    companyId: SAMPLE_COMPANIES[index].id,
    stageId,
    gcAmountMax,
    gcAmountMin: gcAmountMax / 2,
    raiseAmount,
  });

/** Four live stages for the frosted Pipeline module, a handful of sample deals each. */
export const PREVIEW_PIPELINE: IPreviewPipelineColumn[] = [
  {
    title: 'New',
    deals: [
      deal(0, 1, 2_000_000, 8_000_000),
      deal(1, 1, 1_500_000, 5_000_000),
      deal(2, 1, 3_000_000, 12_000_000),
    ],
  },
  { title: 'Screening', deals: [deal(3, 2, 2_500_000, 10_000_000), deal(4, 2, 4_000_000, 20_000_000)] },
  {
    title: 'Diligence',
    deals: [
      deal(5, 3, 5_000_000, 25_000_000),
      deal(6, 3, 3_500_000, 15_000_000),
      deal(7, 3, 6_000_000, 30_000_000),
    ],
  },
  { title: 'Term Sheet', deals: [deal(8, 4, 7_500_000, 40_000_000), deal(9, 4, 5_000_000, 22_000_000)] },
];

export const previewCompanyName = (companyId: number): string =>
  SAMPLE_COMPANIES.find((company) => company.id === companyId)?.name ?? '';

export const PREVIEW_DEAL_TYPES = ['Venture', 'Growth', 'Secondaries'];

const LEADS = ['Jordan Ellis', 'Priya Natarajan', 'Marcus Hale', 'Elena Marsh'];
const ROUNDS = ['Seed', 'Series A', 'Series B', 'Series C'];
const SECTORS = ['Software', 'Healthcare', 'Fintech', 'Robotics'];

export interface IPreviewCategoryDealRow {
  companyName: string;
  dealType: string;
  stage: string;
  round: string;
  raiseAmount: number;
  dealLead: string;
  sector: string;
  updatedAt: string;
}

/** The passed, missed, tracked and closed lists all read as one table of deals that left the board. */
export const PREVIEW_CATEGORY_DEALS: IPreviewCategoryDealRow[] = SAMPLE_COMPANIES.slice(0, 8).map(
  (company, index) => ({
    companyName: company.name,
    dealType: PREVIEW_DEAL_TYPES[index % 2],
    stage: PREVIEW_PIPELINE[index % PREVIEW_PIPELINE.length].title,
    round: ROUNDS[index % ROUNDS.length],
    raiseAmount: 5_000_000 + index * 3_250_000,
    dealLead: LEADS[index % LEADS.length],
    sector: SECTORS[index % SECTORS.length],
    updatedAt: `2026-0${(index % 6) + 3}-1${index % 9}`,
  })
);

/** A funnel that narrows stage by stage, the shape the analytics charts are built for. */
export const PREVIEW_CONVERSION_RATES: IConversionRateDataModel[] = [
  createConversionRateDataModel({
    stage: 'New',
    numOfDeals: 48,
    previousPeriodDifference: 6,
    track: 5,
    pass: 12,
    missed: 3,
    conversionRate: 0.58,
    averageDuration: 9,
    stageSortOrder: 1,
    trackPreviousPeriodDifference: 1,
    passPreviousPeriodDifference: -2,
    missedPreviousPeriodDifference: 0,
  }),
  createConversionRateDataModel({
    stage: 'Screening',
    numOfDeals: 28,
    previousPeriodDifference: 3,
    track: 3,
    pass: 8,
    missed: 1,
    conversionRate: 0.57,
    averageDuration: 14,
    stageSortOrder: 2,
    trackPreviousPeriodDifference: 0,
    passPreviousPeriodDifference: 1,
    missedPreviousPeriodDifference: -1,
  }),
  createConversionRateDataModel({
    stage: 'Diligence',
    numOfDeals: 16,
    previousPeriodDifference: -2,
    track: 2,
    pass: 6,
    missed: 1,
    conversionRate: 0.44,
    averageDuration: 31,
    stageSortOrder: 3,
    trackPreviousPeriodDifference: 1,
    passPreviousPeriodDifference: 0,
    missedPreviousPeriodDifference: 0,
  }),
  createConversionRateDataModel({
    stage: 'Term Sheet',
    numOfDeals: 7,
    previousPeriodDifference: 1,
    track: 0,
    pass: 2,
    missed: 1,
    conversionRate: 0.57,
    averageDuration: 12,
    stageSortOrder: 4,
    trackPreviousPeriodDifference: 0,
    passPreviousPeriodDifference: 0,
    missedPreviousPeriodDifference: 1,
  }),
  createConversionRateDataModel({
    stage: 'Closed',
    numOfDeals: 4,
    previousPeriodDifference: 1,
    track: 0,
    pass: 0,
    missed: 0,
    averageDuration: 8,
    stageSortOrder: 5,
  }),
];

export const PREVIEW_DEALS_GROUPED: IDealsGroupedDataModel[] = [
  { name: 'Software', value: 18, percentageDifference: 12 },
  { name: 'Healthcare', value: 11, percentageDifference: -4 },
  { name: 'Fintech', value: 9, percentageDifference: 22 },
  { name: 'Robotics', value: 6, percentageDifference: 0 },
  { name: 'Energy', value: 4, percentageDifference: null },
];

/** Stage durations for the live analytics grid: one entry per sample deal, days per stage. */
export const PREVIEW_DEAL_STATS: IDealStatsDataModel[] = SAMPLE_COMPANIES.slice(0, 10).map(
  (company, index) => ({
    deal: createDealStatItemDataModel({
      id: -950_001 - index,
      roundId: index % 4,
      company: { logoUrl: '', name: company.name },
      createdAt: `2026-0${(index % 5) + 1}-0${(index % 8) + 1}T00:00:00.000Z`,
    }),
    stages: [
      { name: 'New', duration: 3 + index, sortOrder: 1 },
      { name: 'Screening', duration: 6 + (index % 5) * 2, sortOrder: 2 },
      { name: 'Diligence', duration: 12 + (index % 4) * 5, sortOrder: 3 },
      { name: 'Term Sheet', duration: 4 + (index % 3) * 3, sortOrder: 4 },
    ],
  })
);

/*
 * The board's own configuration and deals, so the live board, lists and header render the
 * sample: four working stages plus the four the categories are named after.
 */
const stage = (id: number, name: string): IDealStageDataModel => ({
  id,
  name,
  displayName: name,
  sortOrder: id,
  meta: { required: [] },
  default: id === 1,
  createdBy: null,
  updatedBy: null,
  createdAt: '2025-01-01T00:00:00.000Z',
  updatedAt: '2025-01-01T00:00:00.000Z',
});

export const PREVIEW_DEAL_STAGES: IDealStageDataModel[] = [
  ...PREVIEW_PIPELINE.map((column, index) => stage(index + 1, column.title)),
  stage(5, 'Pass'),
  stage(6, 'Missed'),
  stage(7, 'Track'),
  stage(8, 'Closed'),
];

export const PREVIEW_DEAL_TYPE_ID = 1;

export const PREVIEW_DEAL_BOARD_CONFIG: DealBoardConfig = {
  dealStages: PREVIEW_DEAL_STAGES,
  dealTypes: PREVIEW_DEAL_TYPES.map((displayName, index) =>
    createDealTypeDataModel({
      id: index + 1,
      name: displayName.toLowerCase(),
      displayName,
      sortOrder: index + 1,
      dealStageIds: PREVIEW_DEAL_STAGES.map((dealStage) => dealStage.id),
      default: index === 0,
    })
  ),
  sourceTypes: [],
  passReasons: [],
  missedReasons: [],
  todoList: [],
};

const CATEGORY_STAGE_IDS: Record<string, number> = {
  [DealBoardDealCategory.PASS]: 5,
  [DealBoardDealCategory.MISSED]: 6,
  [DealBoardDealCategory.TRACK]: 7,
  [DealBoardDealCategory.CLOSED]: 8,
};

const UPDATED_AT = '2026-08-15T09:00:00.000Z';

/** Every sample deal: the board's, plus a few in each stage the categories read from. */
export const PREVIEW_DEALS: IDealDataModel[] = [
  ...PREVIEW_PIPELINE.flatMap((column) => column.deals).map((dealModel) => ({
    ...dealModel,
    dealTypeId: PREVIEW_DEAL_TYPE_ID,
    stageUpdateDate: UPDATED_AT,
    updatedAt: UPDATED_AT,
  })),
  ...Object.values(CATEGORY_STAGE_IDS).flatMap((stageId, categoryIndex) =>
    [0, 1, 2].map((offset) => {
      const index = (categoryIndex * 3 + offset) % SAMPLE_COMPANIES.length;
      return createDealDataModel({
        id: -800_101 - categoryIndex * 3 - offset,
        companyId: SAMPLE_COMPANIES[index].id,
        stageId,
        dealTypeId: PREVIEW_DEAL_TYPE_ID,
        raiseAmount: 5_000_000 + index * 3_250_000,
        gcAmountMax: 2_000_000 + index * 500_000,
        gcAmountMin: 1_000_000 + index * 250_000,
        stageUpdateDate: UPDATED_AT,
        updatedAt: UPDATED_AT,
      });
    })
  ),
];
